import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, PACKAGING_TYPES, type Packaging } from "../lib/api";
import { Field } from "../components/Field";
import { PlatformMark } from "../components/PlatformMark";
import { money } from "../lib/format";
import { describeRef } from "../lib/tieback";
import { ScopePicker, type Scope } from "../components/ScopePicker";

const PLATFORMS = [
  "ebay", "poshmark", "mercari", "depop", "facebook", "etsy", "grailed", "vinted", "local",
];

const PACKAGING_LABEL: Record<Packaging, string> = {
  poly: "Poly mailer",
  bubble: "Bubble mailer",
  other: "Something else",
  none: "No packaging cost",
};

interface SaleRow {
  saleId: string;
  platform: string;
  salePrice: string | null;
  soldAt: string | null;
  shippedAt: string | null;
  externalOrderId: string | null;
  /** poly | bubble | other | none — what it shipped in. */
  packaging: string | null;
  packagingCost: string | null;
  /** Sold through a boosted listing (Depop): adds the boost % to the fee. */
  boosted: boolean;
  /** The most recent shipping-label cost on the item, if one was recorded. */
  labelCostId: string | null;
  labelCost: string | null;
  itemId: string;
  sku: string;
  isPersonal: boolean;
  title: string | null;
  brand: string | null;
  category: string | null;
  purchasePrice: string | null;
  netProfit: string | null;
  daysToSale: number | null;
  thumbUrl: string | null;
}

interface Draft {
  platform: string; salePrice: string; soldAt: string; externalOrderId: string;
  purchasePrice: string; packaging: Packaging; labelCost: string; boosted: boolean;
}

export function Sales() {
  const [rows, setRows] = useState<SaleRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft>({
    platform: "ebay", salePrice: "", soldAt: "", externalOrderId: "",
    purchasePrice: "", packaging: "other", labelCost: "", boosted: false,
  });
  const [busy, setBusy] = useState<string | null>(null);
  const [scope, setScope] = useState<Scope>("business");
  const [mailer, setMailer] = useState<Record<string, number> | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const res = await api.get<{ sales: SaleRow[] }>(`/sales?scope=${scope}`);
      setRows(res.sales);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load your sales");
    }
  }, [scope]);
  useEffect(() => { void load(); }, [load]);

  // Mailer prices, so the "shipped in" choice shows what each one costs.
  useEffect(() => {
    void (async () => {
      try {
        const s = await api.get<{ settings: Record<string, unknown> }>("/settings");
        setMailer({
          poly: Number(s.settings?.polyMailerCost ?? 0),
          bubble: Number(s.settings?.bubbleMailerCost ?? 0),
          other: Number(s.settings?.avgPackagingCost ?? 0),
          none: 0,
        });
      } catch { /* labels just go unpriced */ }
    })();
  }, []);

  const filtered = useMemo(() => {
    const n = q.trim().toLowerCase();
    if (!n) return rows;
    return rows.filter((r) =>
      [r.title, r.brand, r.sku, r.category, r.platform].filter(Boolean).join(" ").toLowerCase().includes(n)
    );
  }, [rows, q]);

  const totals = useMemo(() => {
    const revenue = rows.reduce((s, r) => s + Number(r.salePrice ?? 0), 0);
    const profit = rows.reduce((s, r) => s + Number(r.netProfit ?? 0), 0);
    return { revenue, profit, count: rows.length };
  }, [rows]);

  function startEdit(r: SaleRow) {
    setEditing(r.saleId);
    setDraft({
      platform: r.platform,
      salePrice: String(r.salePrice ?? ""),
      soldAt: r.soldAt ? String(r.soldAt).slice(0, 10) : "",
      externalOrderId: r.externalOrderId ?? "",
      purchasePrice: String(r.purchasePrice ?? ""),
      packaging: (PACKAGING_TYPES as readonly string[]).includes(r.packaging ?? "")
        ? (r.packaging as Packaging)
        : r.packagingCost != null ? "other" : "none",
      labelCost: String(r.labelCost ?? ""),
      boosted: r.boosted,
    });
  }

  /**
   * The sale itself, plus the three costs that decide its profit: what you
   * paid (an item field), the mailer (replaces the packaging cost row), and
   * the shipping label (a cost row of its own, created or corrected here).
   */
  async function saveEdit(r: SaleRow) {
    const saleId = r.saleId;
    setBusy(saleId);
    setError(null);
    try {
      const patch: Record<string, unknown> = { platform: draft.platform };
      if (draft.salePrice !== "") patch.salePrice = Number(draft.salePrice);
      if (draft.soldAt !== "") patch.soldAt = draft.soldAt;
      patch.externalOrderId = draft.externalOrderId.trim() || null;
      if (draft.packaging !== (r.packaging ?? "")) patch.packaging = draft.packaging;
      if (draft.boosted !== r.boosted) patch.boosted = draft.boosted;
      await api.patch(`/sales/${saleId}`, patch);

      const paid = draft.purchasePrice.trim();
      if (paid !== "" && Number(paid) !== Number(r.purchasePrice ?? NaN)) {
        await api.patch(`/items/${r.itemId}`, { purchasePrice: Number(paid) });
      }

      const label = draft.labelCost.trim();
      if (label === "" && r.labelCostId) {
        await api.del(`/costs/${r.labelCostId}`);
      } else if (label !== "" && Number(label) !== Number(r.labelCost ?? NaN)) {
        if (r.labelCostId) {
          await api.patch(`/costs/${r.labelCostId}`, { amount: Number(label), isEstimate: false });
        } else {
          await api.post(`/items/${r.itemId}/costs`, {
            kind: "shipping_label", amount: Number(label), note: "Shipping label",
          });
        }
      }

      setEditing(null);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save that sale");
    } finally {
      setBusy(null);
    }
  }

  async function remove(r: SaleRow) {
    if (!window.confirm(`Delete this sale and return "${r.title ?? r.sku}" to your inventory?`)) return;
    setBusy(r.saleId);
    setError(null);
    try {
      await api.del(`/sales/${r.saleId}`);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not delete that sale");
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="shell">
      <header className="masthead">
        <span className="wordmark">sold</span>
        <span className="num" style={{ color: "var(--muted)" }}>{rows.length}</span>
      </header>

      {error && <div className="notice">{error}</div>}

      <ScopePicker scope={scope} onChange={setScope} />

      <section className="ledger">
        <div>
          <span className="eyebrow">Items sold</span>
          <span className="value">{totals.count}</span>
        </div>
        <div>
          <span className="eyebrow">Revenue</span>
          <span className="value">{money(totals.revenue)}</span>
        </div>
        <div>
          <span className="eyebrow">Net profit</span>
          <span className={`value ${totals.profit >= 0 ? "pos" : "neg"}`}>{money(totals.profit)}</span>
        </div>
      </section>

      <section className="rail">
        <h2 className="eyebrow rail-label">Everything you've sold</h2>
        <input
          className="search"
          type="search"
          placeholder="Search title, brand, SKU, platform…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />

        {filtered.length === 0 ? (
          <div className="empty">
            <p>{rows.length === 0 ? "Nothing sold yet." : "No sales match that search."}</p>
          </div>
        ) : (
          filtered.map((r) => {
            const profit = Number(r.netProfit ?? 0);
            return (
              <div key={r.saleId} className="sale-row">
                <div className="tag" style={{ cursor: "default" }}>
                  {r.thumbUrl ? (
                    <span className="tag-thumb tag-thumb-img"><img src={r.thumbUrl} alt="" loading="lazy" /></span>
                  ) : (
                    <PlatformMark platform={r.platform} />
                  )}
                  <span className="tag-body">
                    <span className="tag-title">
                      <Link to={`/items/${r.itemId}`}>{r.title ?? r.sku}</Link>
                    </span>
                    <span className="tag-meta">
                      <span className="num">{r.sku}</span>
                      {r.isPersonal && <span className="personal-flag">personal</span>}
                      <span style={{ textTransform: "capitalize" }}>{r.platform}</span>
                      <span title="What you paid">paid {money(r.purchasePrice)}</span>
                      {r.soldAt && <span>{new Date(r.soldAt).toLocaleDateString()}</span>}
                      {r.daysToSale != null && <span className="num">{r.daysToSale}d to sell</span>}
                      {r.externalOrderId
                        ? <span title={r.externalOrderId}>{describeRef(r.externalOrderId)}</span>
                        : <span style={{ color: "var(--muted)" }}>not tied to a report</span>}
                    </span>
                  </span>
                  <span className="tag-price">
                    <span className="ask">{money(r.salePrice)}</span>
                    <span className={`listing-profit ${profit >= 0 ? "pos" : "neg"}`}>
                      net {money(r.netProfit)}
                    </span>
                  </span>
                </div>

                {editing === r.saleId ? (
                  <div className="sale-edit">
                    <div className="row">
                      <Field label="Platform">
                        <select
                          value={draft.platform}
                          onChange={(e) => setDraft((d) => ({ ...d, platform: e.target.value }))}
                        >
                          {PLATFORMS.map((p) => <option key={p} value={p}>{p}</option>)}
                        </select>
                      </Field>
                      <Field label="Final price">
                        <input
                          className="money" type="number" step="0.01" min="0"
                          value={draft.salePrice}
                          onChange={(e) => setDraft((d) => ({ ...d, salePrice: e.target.value }))}
                        />
                      </Field>
                      <Field label="Sold on">
                        <input
                          type="date"
                          value={draft.soldAt}
                          onChange={(e) => setDraft((d) => ({ ...d, soldAt: e.target.value }))}
                        />
                      </Field>
                    </div>
                    <div className="row">
                      <Field
                        label="Order reference"
                        hint="platform:buyer:YYYYMMDD/SKU — how this sale ties to the sales report"
                      >
                        <input
                          type="text" autoCapitalize="none" spellCheck={false}
                          style={{ fontFamily: "ui-monospace, monospace" }}
                          placeholder="depop:obiwh_948:20260729/T-000269"
                          value={draft.externalOrderId}
                          onChange={(e) => setDraft((d) => ({ ...d, externalOrderId: e.target.value }))}
                        />
                      </Field>
                    </div>
                    <h3 className="eyebrow" style={{ margin: "0.25rem 0 0.5rem" }}>Costs</h3>
                    <div className="row">
                      <Field label="What you paid" hint="Purchase price of the item">
                        <input
                          className="money" type="number" step="0.01" min="0"
                          value={draft.purchasePrice}
                          onChange={(e) => setDraft((d) => ({ ...d, purchasePrice: e.target.value }))}
                        />
                      </Field>
                      <Field label="Shipped in" hint="Replaces the packaging charge on this sale">
                        <select
                          value={draft.packaging}
                          onChange={(e) => setDraft((d) => ({ ...d, packaging: e.target.value as Packaging }))}
                        >
                          {PACKAGING_TYPES.map((p) => (
                            <option key={p} value={p}>
                              {PACKAGING_LABEL[p]}{mailer && p !== "none" ? ` — ${money(mailer[p])}` : ""}
                            </option>
                          ))}
                        </select>
                      </Field>
                      <Field label="Shipping label" hint="Leave blank if the buyer or platform covered it">
                        <input
                          className="money" type="number" step="0.01" min="0" placeholder="0.00"
                          value={draft.labelCost}
                          onChange={(e) => setDraft((d) => ({ ...d, labelCost: e.target.value }))}
                        />
                      </Field>
                    </div>
                    {draft.platform === "depop" && (
                      <label className="toggle-row">
                        <input
                          type="checkbox"
                          style={{ width: "auto" }}
                          checked={draft.boosted}
                          onChange={(e) => setDraft((d) => ({ ...d, boosted: e.target.checked }))}
                        />
                        <span>Sold with Boost — adds Depop's boost fee to this sale</span>
                      </label>
                    )}
                    <p className="hint" style={{ marginTop: 0 }}>
                      Other costs (laundering, repair, promotion) live on the{" "}
                      <Link to={`/items/${r.itemId}`}>item's page</Link>.
                    </p>
                    <div className="actions">
                      <button onClick={() => void saveEdit(r)} disabled={busy === r.saleId}>
                        {busy === r.saleId ? "Saving" : "Save"}
                      </button>
                      <button className="quiet" onClick={() => setEditing(null)} disabled={busy === r.saleId}>
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="row-actions tag-indent">
                    <button className="quiet" onClick={() => startEdit(r)} disabled={busy === r.saleId}>
                      Edit
                    </button>
                    <button className="quiet danger" onClick={() => void remove(r)} disabled={busy === r.saleId}>
                      Delete
                    </button>
                  </div>
                )}
              </div>
            );
          })
        )}
      </section>
    </div>
  );
}
